import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Building2Icon, ContactRoundIcon } from 'lucide-react';
import {
  Avatar,
  BackLink,
  DataTable,
  EmptyState,
  ErrorNotice,
  FactList,
  PageEyebrow,
  PageHeading,
  Pagination,
  Panel,
  Pill,
  SearchInput,
  SegmentedControl,
  SkeletonTable,
  Tabs,
} from '@bcecloud/portal-ui';
import { errorMessage, getJson } from '@/lib/api';
import { EventsTable } from '@/components/EventsTable';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { useUrlPage, useUrlSearch } from '@/lib/use-list-query-state';

/**
 * Client accounts, keyed by SALESFORCE account id — stable across renames and
 * the hook for parent/child linking. INTERNAL-ONLY (the routes 403 everyone
 * else); contractors keep their events list without these rollups. The
 * account page follows the person-record grammar: identity header, fact
 * list, then related lists in tabs.
 */

const ACCOUNTS_PAGE_SIZE = 25;

interface AccountFacet {
  readonly accountId: string;
  readonly name: string;
  readonly count: number;
  readonly upcomingCount: number;
  readonly lastEventAt: string | null;
}

interface AccountDetail {
  readonly id: string;
  readonly name: string;
  readonly type: string | null;
  readonly eventCount: number;
  readonly firstEventAt: string | null;
  readonly lastEventAt: string | null;
  readonly salesforce: {
    readonly billing: string | null;
    readonly phone: string | null;
    readonly parent: { readonly id: string; readonly name: string } | null;
    readonly children: ReadonlyArray<{ readonly id: string; readonly name: string }>;
  } | null;
}

interface AccountContactRow {
  readonly email: string;
  readonly eventCount: number;
  readonly contactId: string | null;
  readonly name: string;
  readonly phone: string;
  readonly roles: readonly string[];
}

/** A contact with its roles joined, so the search can match them. */
interface AccountContactSearchRow extends AccountContactRow {
  readonly rolesText: string;
}

/** The fields each account-page search matches. */
const CONTACT_SEARCH_FIELDS: ReadonlyArray<keyof AccountContactSearchRow> = ['name', 'email', 'phone', 'rolesText'];
const CHILD_SEARCH_FIELDS: ReadonlyArray<'name'> = ['name'];

/** The account page's tabs search independently, each under its own URL key. */
const ACCOUNT_SEARCH_KEYS = { contacts: 'contactsQ', children: 'childrenQ' } as const;

export function AccountsPage(): React.ReactElement {
  const { search, setSearch } = useUrlSearch();
  const { page, setPage } = useUrlPage();
  const { sort, onSort } = useTableSort(['name', 'count', 'upcomingCount', 'lastEventAt']);
  const facets = useQuery({
    queryKey: ['event-facets', 'account'],
    queryFn: ({ signal }) => getJson<AccountFacet[]>('/events/facets', { by: 'account' }, signal),
  });
  const rows =
    facets.data === undefined ? [] : sortRows(filterRows(facets.data, search, ['name']), sort);
  const totalPages = Math.max(1, Math.ceil(rows.length / ACCOUNTS_PAGE_SIZE));
  const visible = rows.slice((page - 1) * ACCOUNTS_PAGE_SIZE, page * ACCOUNTS_PAGE_SIZE);

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="PEOPLE"
        title="Client accounts"
        subtitle="Every account behind your events — click through for the account, its contacts and its events."
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput
          label="Search accounts"
          placeholder="Account name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="w-full max-w-sm"
        />
      </div>
      {facets.isPending ? (
        <SkeletonTable label="Loading accounts…" />
      ) : facets.isError ? (
        <ErrorNotice title="Accounts unavailable" message={errorMessage(facets.error)} />
      ) : rows.length === 0 ? (
        <Panel>
          <EmptyState
            icon={<Building2Icon aria-hidden className="size-6" />}
            title={search === '' ? 'No accounts yet' : 'No matches'}
            message={
              search === ''
                ? 'Accounts appear here from the events you can see.'
                : `Nothing matches "${search}".`
            }
          />
        </Panel>
      ) : (
        <>
          <DataTable
            headers={[
              { label: 'Account', sortKey: 'name' },
              { label: 'Events', sortKey: 'count' },
              { label: 'Upcoming', sortKey: 'upcomingCount' },
              { label: 'Last event', sortKey: 'lastEventAt' },
            ]}
            minWidth={640}
            {...(sort ? { sort } : {})}
            onSort={onSort}
          >
            {visible.map((facet) => (
              <tr key={facet.accountId} className="border-t border-portal-line">
                <td className="p-3">
                  <Link
                    to={`/accounts/${facet.accountId}`}
                    className="text-portal-small font-bold text-brand hover:underline"
                  >
                    {facet.name || facet.accountId}
                  </Link>
                </td>
                <td className="p-3">
                  <Pill>{facet.count}</Pill>
                </td>
                <td className="p-3">
                  {facet.upcomingCount > 0 ? (
                    <Pill tone="success">{facet.upcomingCount}</Pill>
                  ) : (
                    <span className="text-portal-small text-portal-muted">—</span>
                  )}
                </td>
                <td className="p-3 whitespace-nowrap text-portal-small text-portal-muted">
                  {facet.lastEventAt
                    ? new Date(facet.lastEventAt).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                      })
                    : '—'}
                </td>
              </tr>
            ))}
          </DataTable>
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            summary={`${rows.length} account${rows.length === 1 ? '' : 's'}`}
          />
        </>
      )}
    </section>
  );
}

const ACCOUNT_TABS = ['events', 'contacts', 'children'] as const;
type AccountTab = (typeof ACCOUNT_TABS)[number];

/** Which events the Events tab shows. */
const EVENT_SCOPE = { self: 'self', family: 'family' } as const;
type EventScope = (typeof EVENT_SCOPE)[keyof typeof EVENT_SCOPE];

export function AccountPage(): React.ReactElement {
  const { sfId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = (ACCOUNT_TABS as readonly string[]).includes(params.get('tab') ?? '')
    ? (params.get('tab') as AccountTab)
    : 'events';
  const [scope, setScope] = useState<EventScope>(EVENT_SCOPE.self);
  const contactSearch = useUrlSearch(ACCOUNT_SEARCH_KEYS.contacts);
  const childSearch = useUrlSearch(ACCOUNT_SEARCH_KEYS.children);

  const account = useQuery({
    queryKey: ['account', sfId],
    queryFn: ({ signal }) => getJson<AccountDetail>(`/accounts/${sfId}`, undefined, signal),
    enabled: sfId.length >= 15,
  });
  const contacts = useQuery({
    queryKey: ['account-contacts', sfId],
    queryFn: ({ signal }) =>
      getJson<AccountContactRow[]>(`/accounts/${sfId}/contacts`, undefined, signal),
    enabled: sfId.length >= 15,
  });

  if (account.isPending) return <SkeletonTable label="Loading account…" />;
  if (account.isError) {
    return <ErrorNotice title="Account unavailable" message={errorMessage(account.error)} />;
  }
  const detail = account.data;
  const sf = detail.salesforce;
  const children = sf?.children ?? [];
  const familyIds = [detail.id, ...children.map((child) => child.id)].join(',');
  const contactRows = filterRows(
    (contacts.data ?? []).map((contact) => ({ ...contact, rolesText: contact.roles.join(', ') })),
    contactSearch.search,
    CONTACT_SEARCH_FIELDS,
  );
  const childRows = filterRows(children, childSearch.search, CHILD_SEARCH_FIELDS);

  return (
    <section className="animate-portal-rise">
      <BackLink to="/accounts">Accounts</BackLink>

      {/* Identity header — same grammar as the person and contact records. */}
      <div className="mt-3 mb-[27px] flex flex-wrap items-center gap-4">
        <Avatar name={detail.name || detail.id} size={56} />
        <div className="min-w-0 flex-1">
          <PageEyebrow>CLIENT ACCOUNT</PageEyebrow>
          <h1 className="text-portal-page tracking-[-1px]">{detail.name || detail.id}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            {detail.type ? <Pill>{detail.type}</Pill> : null}
            {sf?.parent ? (
              <span className="text-portal-small text-portal-muted">
                Parent:{' '}
                <Link
                  to={`/accounts/${sf.parent.id}`}
                  className="font-bold text-brand hover:underline"
                >
                  {sf.parent.name}
                </Link>
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <Panel className="mb-6 p-5">
        <FactList
          columns={2}
          facts={[
            { label: 'Account type', value: detail.type ?? undefined },
            { label: 'Location', value: sf?.billing ?? undefined },
            { label: 'Phone', value: sf?.phone ?? undefined },
            { label: 'Parent account', value: sf?.parent?.name },
            { label: 'Events you can see', value: String(detail.eventCount) },
            {
              label: 'Last event',
              value: detail.lastEventAt
                ? new Date(detail.lastEventAt).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                : undefined,
            },
          ]}
        />
      </Panel>

      <Tabs<AccountTab>
        label="Account sections"
        items={[
          { key: 'events', label: 'Events' },
          { key: 'contacts', label: 'Contacts', count: contacts.data?.length ?? undefined },
          { key: 'children', label: 'Child accounts', count: children.length || undefined },
        ]}
        active={tab}
        onChange={(next) =>
          setParams(
            (previous) => {
              const nextParams = new URLSearchParams(previous);
              nextParams.set('tab', next);
              return nextParams;
            },
            { replace: true },
          )
        }
      />

      {tab === 'events' ? (
        <div className="mt-5 space-y-6">
          {children.length > 0 ? (
            <SegmentedControl
              label="Which events to show"
              options={[
                { key: EVENT_SCOPE.self, label: 'This account' },
                { key: EVENT_SCOPE.family, label: `With child accounts (${children.length})` },
              ]}
              value={scope}
              onChange={(next) => setScope(next as EventScope)}
            />
          ) : null}
          <div>
            <h2 className="mb-2 text-portal-section font-bold text-portal-ink">Upcoming events</h2>
            <EventsTable
              filter={{
                accountId: scope === EVENT_SCOPE.family ? familyIds : detail.id,
                state: 'upcoming',
              }}
            />
          </div>
          <div>
            <h2 className="mb-2 text-portal-section font-bold text-portal-ink">Past events</h2>
            <EventsTable
              filter={{
                accountId: scope === EVENT_SCOPE.family ? familyIds : detail.id,
                state: 'past',
              }}
            />
          </div>
        </div>
      ) : null}

      {tab === 'contacts' ? (
        <div className="mt-5">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <SearchInput
              label="Search contacts"
              placeholder="Name, email, phone or role"
              value={contactSearch.search}
              onChange={(event) => contactSearch.setSearch(event.target.value)}
              className="w-full max-w-sm"
            />
          </div>
          {contacts.isPending ? (
            <SkeletonTable label="Loading contacts…" />
          ) : contacts.isError ? (
            <ErrorNotice title="Contacts unavailable" message={errorMessage(contacts.error)} />
          ) : contacts.data.length === 0 ? (
            <Panel>
              <EmptyState
                icon={<ContactRoundIcon aria-hidden className="size-6" />}
                title="No contacts found"
                message="No one is listed as a contact on this account's events."
              />
            </Panel>
          ) : contactRows.length === 0 ? (
            <Panel>
              <EmptyState title="No contacts match" message="Try a different search." />
            </Panel>
          ) : (
            <DataTable headers={['Contact', 'Phone', 'Roles', 'Events']} minWidth={680}>
              {contactRows.map((contact) => (
                <tr key={contact.email} className="border-t border-portal-line align-top">
                  <td className="p-3">
                    {contact.contactId ? (
                      <Link
                        to={`/contacts/${contact.contactId}`}
                        className="text-portal-small font-bold text-brand hover:underline"
                      >
                        {contact.name || contact.email}
                      </Link>
                    ) : (
                      <span className="text-portal-small font-bold text-portal-ink">
                        {contact.name || contact.email}
                      </span>
                    )}
                    <span className="block truncate text-portal-micro text-portal-muted">
                      {contact.email}
                    </span>
                  </td>
                  <td className="p-3 whitespace-nowrap text-portal-small text-portal-muted">
                    {contact.phone || '—'}
                  </td>
                  <td className="p-3 text-portal-small text-portal-muted">
                    {contact.rolesText || '—'}
                  </td>
                  <td className="p-3">
                    <Pill>{contact.eventCount}</Pill>
                  </td>
                </tr>
              ))}
            </DataTable>
          )}
        </div>
      ) : null}

      {tab === 'children' ? (
        <div className="mt-5">
          {children.length > 0 ? (
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <SearchInput
                label="Search child accounts"
                placeholder="Account name"
                value={childSearch.search}
                onChange={(event) => childSearch.setSearch(event.target.value)}
                className="w-full max-w-sm"
              />
            </div>
          ) : null}
          {children.length === 0 ? (
            <Panel>
              <EmptyState
                icon={<Building2Icon aria-hidden className="size-6" />}
                title="No child accounts"
                message={
                  sf === null
                    ? 'Salesforce is unavailable, so parent/child links cannot be shown right now.'
                    : 'Salesforce lists no accounts under this one.'
                }
              />
            </Panel>
          ) : childRows.length === 0 ? (
            <Panel>
              <EmptyState title="No child accounts match" message="Try a different search." />
            </Panel>
          ) : (
            <DataTable headers={['Account', '']} minWidth={480}>
              {childRows.map((child) => (
                <tr key={child.id} className="border-t border-portal-line">
                  <td className="p-3">
                    <Link
                      to={`/accounts/${child.id}`}
                      className="text-portal-small font-bold text-brand hover:underline"
                    >
                      {child.name || child.id}
                    </Link>
                  </td>
                  <td className="p-3 text-right">
                    <Link
                      to={`/accounts/${child.id}`}
                      className="text-portal-micro font-bold text-brand hover:underline"
                    >
                      Open →
                    </Link>
                  </td>
                </tr>
              ))}
            </DataTable>
          )}
        </div>
      ) : null}
    </section>
  );
}
